// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import {
    LosslessNumber,
    isLosslessNumber,
    isSafeNumber,
    parse,
    stringify,
    type DuplicateKeyInfo,
    type NumberStringifier,
    type Replacer,
} from 'lossless-json';
import { z } from 'zod';

export { LosslessNumber, isLosslessNumber };

const StructuredClonedLosslessNumberSchema = z
    .object({
        isLosslessNumber: z.literal(true),
        value: z
            .string()
            .refine(
                (value) => SemanticJsonNumberTokenPattern.test(value),
                'Invalid lossless JSON number token'
            ),
    })
    .strict()
    .transform(({ value }) => new LosslessNumber(value));

const StructuredClonedLosslessJsonValueSchema: z.ZodType<unknown> = z.lazy(() =>
    z.union([
        z.null(),
        z.boolean(),
        z.string(),
        z.number().finite(),
        z.undefined(),
        StructuredClonedLosslessNumberSchema,
        z.array(StructuredClonedLosslessJsonValueSchema),
        z
            .record(z.string(), StructuredClonedLosslessJsonValueSchema)
            .refine(
                (value) => !Object.hasOwn(value, 'isLosslessNumber'),
                'Invalid structured-cloned lossless number'
            ),
    ])
);

export function stringifyClonedLosslessJson(value: unknown): string {
    return stringifyLosslessJson(StructuredClonedLosslessJsonValueSchema.parse(value));
}

interface ComparableDecimal {
    readonly sign: -1 | 0 | 1;
    readonly digits: string;
    readonly exponent: bigint;
}

interface ExactDecimal {
    readonly coefficient: bigint;
    readonly scale: number;
}

const MaximumExactDecimalScale = 1_000;

function parseExactDecimal(value: LosslessNumber): ExactDecimal {
    const match =
        /^(?<sign>-)?(?<integer>0|[1-9]\d*)(?:\.(?<fraction>\d+))?(?:[eE](?<exponent>[+-]?\d+))?$/u.exec(
            value.value
        );
    if (match?.groups === undefined) {
        throw new TypeError('Invalid lossless number');
    }
    const exponentBigInt = BigInt(match.groups.exponent ?? '0');
    if (
        exponentBigInt < BigInt(-MaximumExactDecimalScale) ||
        exponentBigInt > BigInt(MaximumExactDecimalScale)
    ) {
        throw new RangeError('Lossless number exponent exceeds exact arithmetic range');
    }
    const exponent = Number(exponentBigInt);
    const fraction = match.groups.fraction ?? '';
    const coefficient = BigInt(`${match.groups.integer ?? '0'}${fraction}`);
    return {
        coefficient: match.groups.sign === undefined ? coefficient : -coefficient,
        scale: fraction.length - exponent,
    };
}

function formatExactDecimal(exactDecimal: ExactDecimal): string {
    if (exactDecimal.coefficient === 0n) {
        return '0';
    }
    const negative = exactDecimal.coefficient < 0n;
    const digits = (negative ? -exactDecimal.coefficient : exactDecimal.coefficient).toString();
    let token: string;
    if (exactDecimal.scale <= 0) {
        token = `${digits}${'0'.repeat(-exactDecimal.scale)}`;
    } else if (digits.length <= exactDecimal.scale) {
        token = `0.${'0'.repeat(exactDecimal.scale - digits.length)}${digits}`;
    } else {
        token = `${digits.slice(0, -exactDecimal.scale)}.${digits.slice(-exactDecimal.scale)}`;
    }
    if (token.includes('.')) {
        token = token.replace(/0+$/u, '').replace(/\.$/u, '');
    }
    return negative ? `-${token}` : token;
}

function exactDecimalToLosslessNumber(exactDecimal: ExactDecimal): LosslessNumber {
    return new LosslessNumber(formatExactDecimal(exactDecimal));
}

export function addLosslessNumbers(values: readonly LosslessNumber[]): LosslessNumber {
    if (values.length === 0) {
        return new LosslessNumber('0');
    }
    const exactDecimals = values.map(parseExactDecimal);
    const scale = Math.max(...exactDecimals.map((value) => value.scale));
    const coefficient = exactDecimals.reduce(
        (sum, value) => sum + value.coefficient * 10n ** BigInt(scale - value.scale),
        0n
    );
    return exactDecimalToLosslessNumber({ coefficient, scale });
}

export function subtractLosslessNumbers(
    left: LosslessNumber,
    right: LosslessNumber
): LosslessNumber {
    const leftDecimal = parseExactDecimal(left);
    const rightDecimal = parseExactDecimal(right);
    const scale = Math.max(leftDecimal.scale, rightDecimal.scale);
    return exactDecimalToLosslessNumber({
        coefficient:
            leftDecimal.coefficient * 10n ** BigInt(scale - leftDecimal.scale) -
            rightDecimal.coefficient * 10n ** BigInt(scale - rightDecimal.scale),
        scale,
    });
}

export function losslessUsdFromSafeIntegerCents(cents: number): LosslessNumber {
    if (!Number.isSafeInteger(cents)) {
        throw new RangeError('USD cents must be a safe integer');
    }
    return exactDecimalToLosslessNumber({ coefficient: BigInt(cents), scale: 2 });
}

export function multiplyLosslessNumbers(
    left: LosslessNumber,
    right: LosslessNumber
): LosslessNumber {
    const leftDecimal = parseExactDecimal(left);
    const rightDecimal = parseExactDecimal(right);
    return exactDecimalToLosslessNumber({
        coefficient: leftDecimal.coefficient * rightDecimal.coefficient,
        scale: leftDecimal.scale + rightDecimal.scale,
    });
}

export function roundLosslessNumber(value: LosslessNumber, decimalPlaces: number): LosslessNumber {
    if (!Number.isSafeInteger(decimalPlaces) || decimalPlaces < 0) {
        throw new RangeError('Decimal places must be a nonnegative safe integer');
    }
    const exactDecimal = parseExactDecimal(value);
    if (exactDecimal.scale <= decimalPlaces) {
        return exactDecimalToLosslessNumber(exactDecimal);
    }
    const divisor = 10n ** BigInt(exactDecimal.scale - decimalPlaces);
    let coefficient = exactDecimal.coefficient / divisor;
    const remainder = exactDecimal.coefficient % divisor;
    if ((remainder < 0n ? -remainder : remainder) * 2n >= divisor) {
        coefficient += exactDecimal.coefficient < 0n ? -1n : 1n;
    }
    return exactDecimalToLosslessNumber({ coefficient, scale: decimalPlaces });
}

export function formatLosslessNumberFixed(value: LosslessNumber, decimalPlaces: number): string {
    return formatPlainDecimal(parseExactDecimal(roundLosslessNumber(value, decimalPlaces)), decimalPlaces);
}

// Traceability: implements OBL-RBS-MONEY-EXACT from implementation LOSSLESS-PLAIN-DECIMAL-PADDED
// Writes every digit of the exact value in plain notation, padding the
// fraction to at least minimumDecimalPlaces and never rounding.
export function formatLosslessNumberPadded(
    value: LosslessNumber,
    minimumDecimalPlaces: number
): string {
    if (!Number.isSafeInteger(minimumDecimalPlaces) || minimumDecimalPlaces < 0) {
        throw new RangeError('Decimal places must be a nonnegative safe integer');
    }
    const exactDecimal = parseExactDecimal(value);
    return formatPlainDecimal(exactDecimal, Math.max(exactDecimal.scale, minimumDecimalPlaces));
}

function formatPlainDecimal(exactDecimal: ExactDecimal, decimalPlaces: number): string {
    const negative = exactDecimal.coefficient < 0n;
    const absoluteCoefficient = negative ? -exactDecimal.coefficient : exactDecimal.coefficient;
    const scaledCoefficient =
        exactDecimal.scale < decimalPlaces
            ? absoluteCoefficient * 10n ** BigInt(decimalPlaces - exactDecimal.scale)
            : absoluteCoefficient;
    const digits = scaledCoefficient.toString().padStart(decimalPlaces + 1, '0');
    const unsignedValue =
        decimalPlaces === 0
            ? digits
            : `${digits.slice(0, -decimalPlaces)}.${digits.slice(-decimalPlaces)}`;
    return negative ? `-${unsignedValue}` : unsignedValue;
}

export function formatLosslessUsd(value: LosslessNumber): string {
    const fixedValue = formatLosslessNumberFixed(value, 2);
    const negative = fixedValue.startsWith('-');
    const unsignedValue = negative ? fixedValue.slice(1) : fixedValue;
    const [whole = '0', fraction = '00'] = unsignedValue.split('.');
    const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/gu, ',');
    return `${negative ? '-' : ''}$${groupedWhole}.${fraction}`;
}

function parseComparableDecimal(value: LosslessNumber): ComparableDecimal {
    const match =
        /^(?<sign>-)?(?<integer>0|[1-9]\d*)(?:\.(?<fraction>\d+))?(?:[eE](?<exponent>[+-]?\d+))?$/u.exec(
            value.value
        );
    if (match?.groups === undefined) {
        throw new TypeError('Invalid lossless number');
    }
    const integer = match.groups.integer ?? '0';
    const unsignedDigits = `${integer}${match.groups.fraction ?? ''}`;
    const firstNonzeroIndex = unsignedDigits.search(/[1-9]/u);
    if (firstNonzeroIndex === -1) {
        return { sign: 0, digits: '0', exponent: 0n };
    }
    const digits = unsignedDigits.slice(firstNonzeroIndex);
    return {
        sign: match.groups.sign === undefined ? 1 : -1,
        digits: digits.replace(/0+$/u, ''),
        exponent:
            BigInt(match.groups.exponent ?? '0') + BigInt(integer.length - firstNonzeroIndex - 1),
    };
}

export function compareLosslessNumber(left: LosslessNumber, right: LosslessNumber): -1 | 0 | 1 {
    const leftDecimal = parseComparableDecimal(left);
    const rightDecimal = parseComparableDecimal(right);
    if (leftDecimal.sign !== rightDecimal.sign) {
        return leftDecimal.sign < rightDecimal.sign ? -1 : 1;
    }
    if (leftDecimal.sign === 0) {
        return 0;
    }
    let magnitudeComparison: -1 | 0 | 1 = 0;
    if (leftDecimal.exponent !== rightDecimal.exponent) {
        magnitudeComparison = leftDecimal.exponent < rightDecimal.exponent ? -1 : 1;
    } else {
        const width = Math.max(leftDecimal.digits.length, rightDecimal.digits.length);
        const leftDigits = leftDecimal.digits.padEnd(width, '0');
        const rightDigits = rightDecimal.digits.padEnd(width, '0');
        magnitudeComparison = leftDigits < rightDigits ? -1 : leftDigits > rightDigits ? 1 : 0;
    }
    return leftDecimal.sign === 1
        ? magnitudeComparison
        : magnitudeComparison === 0
          ? 0
          : magnitudeComparison === 1
            ? -1
            : 1;
}

export type LosslessJsonReplacer = Replacer;

export type LosslessJsonPrimitive = null | boolean | string | number | LosslessNumber;

export type LosslessJsonArray = LosslessJsonValue[];

export type LosslessJsonObject = {
    [key: string]: LosslessJsonValue;
};

export type LosslessJsonValue = LosslessJsonPrimitive | LosslessJsonArray | LosslessJsonObject;

export const SemanticLosslessNumberRepresentationKeys: ReadonlySet<string> = new Set([
    'isLosslessNumber',
    'value',
]);

export const SemanticJsonNumberTokenPattern: RegExp =
    /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/u;

export function isSemanticLosslessNumber(value: unknown): value is LosslessNumber {
    if (!isLosslessNumber(value)) {
        return false;
    }
    if (!SemanticJsonNumberTokenPattern.test(value.value)) {
        return false;
    }
    if (
        !Object.keys(value).every((key: string) =>
            SemanticLosslessNumberRepresentationKeys.has(key)
        )
    ) {
        return false;
    }
    try {
        return value.toString() === value.value;
    } catch {
        return false;
    }
}

export class LosslessJsonSerializationError extends TypeError {
    constructor(message: string, options?: ErrorOptions) {
        super(message, options);
        this.name = 'LosslessJsonSerializationError';
    }
}

export const StrictLosslessNumberStringifier: NumberStringifier = {
    test: isLosslessNumber,
    stringify: (value: unknown): string => {
        if (!isSemanticLosslessNumber(value)) {
            throw new TypeError('Invalid lossless JSON number representation');
        }
        return value.value;
    },
};

export function parseLosslessJson(jsonText: string): unknown {
    return parse(jsonText);
}

function parseNativeSafeNumber(numberToken: string): number | LosslessNumber {
    if (isSafeNumber(numberToken, { approx: false })) {
        return Number.parseFloat(numberToken);
    }
    return new LosslessNumber(numberToken);
}

function useLastDuplicateJsonValue(duplicateKey: DuplicateKeyInfo): unknown {
    return duplicateKey.newValue;
}

export function parseJsonWithNativeSafeNumbers(jsonText: string): unknown {
    return parse(jsonText, null, {
        onDuplicateKey: useLastDuplicateJsonValue,
        parseNumber: parseNativeSafeNumber,
    });
}

export function stringifyLosslessJson(
    value: unknown,
    replacer: Replacer | null = null,
    space?: number | string
): string {
    let jsonText: string | undefined;
    try {
        jsonText = stringify(value, replacer, space, [StrictLosslessNumberStringifier]);
    } catch (cause) {
        throw new LosslessJsonSerializationError('Failed to serialize value as a JSON document', {
            cause,
        });
    }
    if (jsonText === undefined) {
        throw new LosslessJsonSerializationError('Value cannot be serialized as a JSON document');
    }
    return jsonText;
}
